import { createContext, useContext, ReactNode } from 'react';
import { CopyNumber } from './CopyNumber';

interface SectionContextValue {
  /** Numer top-level sekcji (np. "13" dla Buttonów). */
  number: string;
}

const SectionContext = createContext<SectionContextValue | null>(null);

export function SectionNumberProvider({ number, children }: { number: string; children: ReactNode }) {
  return <SectionContext.Provider value={{ number }}>{children}</SectionContext.Provider>;
}

export function useSectionNumber(): string {
  const ctx = useContext(SectionContext);
  return ctx?.number ?? '';
}

export interface SubsectionProps {
  /** Lokalny podnumer w ramach sekcji (1, 2, 3…). Kompozycja: `{parent}.{n}`. */
  n: number;
  /** Tytuł podsekcji wyświetlany obok numeru. */
  title: string;
  /** Opcjonalny opis pod tytułem. */
  description?: ReactNode;
  /** Akcje po prawej (np. link do docs). */
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * Wrapper pojedynczego demo-bloku wewnątrz sekcji Brandingu. Renderuje
 * nagłówek z pełnym numerem (np. "13.3") i chip CopyNumber do kopiowania
 * numeru do clipboardu. User klika → wkleja feedback z numerem ("nie
 * podoba mi się 13.3") zamiast opisywać element słowami.
 */
export function Subsection({ n, title, description, actions, children, className }: SubsectionProps) {
  const parentN = useSectionNumber();
  const full = parentN ? `${parentN}.${n}` : String(n);

  return (
    <div className={['space-y-3', className].filter(Boolean).join(' ')}>
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-base font-semibold text-text-primary leading-tight">
          <span className="font-mono text-accent-primary mr-1.5 text-sm">{full}</span>
          {title}
        </h3>
        <CopyNumber number={full} variant="inline" />
        {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
      </div>
      {description && (
        <p className="text-xs text-text-muted leading-relaxed">{description}</p>
      )}
      <div>{children}</div>
    </div>
  );
}
